/**
 * Notifications Module
 *
 * Unified notification system types, channels, preferences, and action builders.
 */

// Types
export {
  type AnyNotificationPayload,
  type BalanceNegativeData,
  type BudgetThresholdReachedData,
  buildWelcomeChatLink,
  // Helper functions
  createDismissAction,
  createExternalAction,
  createNavigateAction,
  type InterviewApprovedData,
  type InterviewAutoApprovedData,
  type InterviewBookedData,
  type InterviewEarningsPostedData,
  type InterviewInvitationSentData,
  type InterviewRejectedData,
  type InterviewReminderData,
  type NotificationAction,
  NotificationActionType,
  type NotificationActionTypeValue,
  type NotificationContent,
  // Data types per notification
  type NotificationDataMap,
  type NotificationPayload,
  NotificationPriority,
  type NotificationPriorityType,
  // Types
  type NotificationType,
  // Enums and constants
  NotificationTypes,
  type OpportunityDigestData,
  type OpportunityDigestListing,
  type OpportunityListedData,
  type OrganizationInvitationAcceptedData,
  type OrganizationInvitationCreatedData,
  type OrganizationInvitationRevokedData,
  type PaymentClawedBackData,
  type ReferralOutreachRequestData,
  type ResearcherReviewDigestData,
  type ResearcherReviewDigestStage,
  type ResearcherReviewDigestStudy,
  type StudyApprovalDecidedData,
  type StudyApprovalRequestedData,
  type StudyFulfillmentAlertData,
  type StudyStartedData,
  type StudyStuckData,
  type WelcomeData,
} from "./types";

// Channels
export {
  type AvailableChannels,
  availableChannelsPerPreference,
  getAvailableChannelsForType,
  // Helper functions
  getPreferenceKeyForType,
  getPreferenceKeysForCategory,
  isChannelAvailableForType,
  // Types
  type NotificationChannel,
  NotificationChannels,
  notificationTypeToPreferenceKey,
  PreferenceCategories,
  type PreferenceCategory,
  type PreferenceKey,
  PreferenceKeys,
  preferenceKeyToCategory,
  type UserControllableChannel,
} from "./channels";

// Preferences
export {
  defaultPreferences,
  // Helper functions
  getDefaultChannels,
  getPriorityForType,
  isCriticalNotification,
  notificationTypePriority,
} from "./preferences";

// Actions
export {
  buildBalanceNegativeAction,
  buildBudgetThresholdAction,
  buildCompleteMatchProfileAction,
  buildEarningsPostedAction,
  buildFeasibilityQuoteReadyAction,
  buildFeasibilityQuoteReadyRequestsAction,
  buildInterviewApprovedAction,
  buildInterviewAutoApprovedAction,
  buildInterviewBookedExpertAction,
  buildOfficeHoursAnnouncedAction,
  buildInterviewBookedResearcherAction,
  buildInterviewInvitationSentAction,
  buildInterviewRejectedAction,
  buildOrgInvitationAcceptedAction,
  buildOrgInvitationCreatedAction,
  buildOrgInvitationRevokedAction,
  buildPaymentClawedBackAction,
  buildResearcherReviewDigestAction,
  buildStudyApprovalDecidedAction,
  buildStudyApprovalRequestedAction,
  buildStudyBookingNeededAction,
  buildStudyCompletedAction,
  buildStudyStartedAction,
  buildStudyTurnaroundAction,
  iOSDeepLinks,
  webPaths,
} from "./actions";

// Platform Targeting
export {
  getIOSSupportedNotificationTypes,
  getParticipantPreferences,
  // Helper functions
  getPreferencesForRole,
  getResearcherPreferences,
  isPlatformSupported,
  isPushRecommended,
  type NotificationTargeting,
  notificationTargeting,
  type Platform,
  Platforms,
  type PreferenceTargeting,
  preferenceTargeting,
  // Types
  type UserRole,
  UserRoles,
} from "./platform-targeting";

// Referral opportunity alerts
export {
  buildConnectionDigestIdempotencyKey,
  buildOpportunityDigestIdempotencyKey,
  buildOpportunityListedIdempotencyKey,
  buildReferralBrowseAllPath,
  formatConnectionDigestSubject,
  formatOpportunityDigestSubject,
  buildReferralOpportunityPath,
} from "./referral-opportunity-alerts";
